import React, { useEffect, useState } from "react";
import moment from "moment";
import PROCESS_STATES from "@common/constants/processStates";
import PHASE_STATUS from "@common/constants/phaseStatus";
import Temperatures from "../Temperatures/Temperatures";
import Gravity from "../Gravity/Gravity";
import UpdateTargetTempSlider from "../UpdateTargetTempSlider/UpdateTargetTempSlider";
import "./ChartHeader.scss";
import * as recipeService from "@beers/services/recipeService";
import { isCleaningSession } from "@monitor/util";

const ChartHeader = ({
  lastFrequentLog,
  session,
  type,
  active,
  startTime,
  endTime,
  status,
  duration,
}) => {
  const initialValues = {
    currentTemp: 0,
    setpointTemp: 0,
    currentGravity: 0,
    timeTillNextUserAction: 0,
    currentStatus: 0,
  };

  let { currentTemp, setpointTemp, currentGravity, currentStatus } =
    lastFrequentLog && lastFrequentLog.logsList?.length > 0
      ? lastFrequentLog.logsList[0]
      : initialValues;

  const [setpointGravity, setSetPointGravity] = useState(0);

  useEffect(() => {
    if (
      session &&
      !isCleaningSession(session) &&
      !session.is_brewpack &&
      session.beer_recipe_id
    ) {
      recipeService
        .getRecipeById(session.beer_recipe_id)
        .then((response) => {
          if (response && response.fg) {
            setSetPointGravity(response.fg);
          }
        })
        .catch(() => {});
    }
  }, [session?.beer_recipe_id]);

  // SERVING_TEMP_CONTROL_STATE is the only state where CMD_UPDATE_RECIPE has a
  // live effect: the firmware retargets the Peltier off recipe.serving_temperature
  // there and nowhere else (Machine.cpp:634-636). During fermentation the recipe
  // ramp re-asserts its own setpoint every loop, so a slider there would lie.
  const showSlider =
    currentStatus?.procState === PROCESS_STATES.SERVING_TEMP_CONTROL_STATE;

  const hasLiveLog = !!lastFrequentLog && (active === undefined || active);
  const hasSummary =
    !hasLiveLog && !!(status || startTime || endTime || duration);

  return (
    <div>
      {hasLiveLog && (
        <div className="ChartHeader">
          <div className="ChartHeader__list">
            <div style={{ display: "flex" }}>
              {!!setpointTemp && (
                <div className="ChartHeader__item ChartHeader__item--temp">
                  <div className="ChartHeader__item-title">Temperature</div>
                  <Temperatures current={currentTemp} target={setpointTemp} />
                </div>
              )}

              {showSlider && (
                <div className="ChartHeader__item ChartHeader__item--slider">
                  <UpdateTargetTempSlider
                    targetTemp={setpointTemp}
                    session={session}
                  />
                </div>
              )}
            </div>

            {currentGravity && setpointGravity ? (
              <div className="ChartHeader__item ChartHeader__item--gravity">
                <div className="ChartHeader__item-title">Gravity</div>
                <Gravity current={currentGravity} target={setpointGravity} />
              </div>
            ) : (
              <div />
            )}
          </div>
        </div>
      )}

      {hasSummary && (
        <div className="ChartHeader">
          <div className="ChartHeader__list ChartHeader__list--summary">
            <div className="ChartHeader__summary-item">
              <div className="ChartHeader__summary-label">Status</div>
              <div
                className={`ChartHeader__summary-value ChartHeader__summary-value--${(
                  status || "completed"
                )
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {status || "Completed"}
              </div>
            </div>

            {startTime && (
              <div className="ChartHeader__summary-item">
                <div className="ChartHeader__summary-label">Started</div>
                <div className="ChartHeader__summary-value">
                  {moment(startTime).format("MMM D, HH:mm")}
                </div>
              </div>
            )}

            {endTime && (
              <div className="ChartHeader__summary-item">
                <div className="ChartHeader__summary-label">Ended</div>
                <div className="ChartHeader__summary-value">
                  {moment(endTime).format("MMM D, HH:mm")}
                </div>
              </div>
            )}

            {duration && (
              <div className="ChartHeader__summary-item">
                <div className="ChartHeader__summary-label">Duration</div>
                <div className="ChartHeader__summary-value">{duration}</div>
              </div>
            )}

            {type === PHASE_STATUS.FERMENTATION && (
              <React.Fragment>
                {session && session.original_gravity ? (
                  <div className="ChartHeader__summary-item">
                    <div className="ChartHeader__summary-label">
                      Original Gravity
                    </div>
                    <div className="ChartHeader__summary-value ChartHeader__summary-value--yellow">
                      {parseFloat(session.original_gravity / 1000).toFixed(3)}
                    </div>
                  </div>
                ) : null}
                {setpointGravity ? (
                  <div className="ChartHeader__summary-item">
                    <div className="ChartHeader__summary-label">
                      Target Gravity
                    </div>
                    <div className="ChartHeader__summary-value">
                      {parseFloat(
                        setpointGravity > 100
                          ? setpointGravity / 1000
                          : setpointGravity,
                      ).toFixed(3)}
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartHeader;
